import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Typography
} from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { parseLaravelError } from 'services/laravel_api/baseQuery'
import { useGetImpersonationQuery } from 'services/laravel_api/impersonation'
import {
    useConfirmReacceptanceMutation,
    useGetReacceptanceStatusQuery
} from 'services/laravel_api/termsOfService'
import { useAuth } from '../../hooks/use-auth'

export const TermsOfServiceModal = () => {
    const [checked, setChecked] = useState(false)

    const authContext = useAuth()

    const {
        data: reacceptanceStatus,
        isLoading,
        isFetching
    } = useGetReacceptanceStatusQuery(
        authContext.isAuthenticated ? undefined : skipToken
    )

    const { data: impersonation } = useGetImpersonationQuery(
        authContext.isAuthenticated ? undefined : skipToken
    )

    const [
        confirmReacceptance,
        { isLoading: isConfirming, error: confirmationError }
    ] = useConfirmReacceptanceMutation()

    const handleAccept = async () => {
        await confirmReacceptance().unwrap()
    }

    // Only the account holder can accept terms, so the server refuses to record an
    // acceptance during impersonation. Showing the dialog anyway would trap the
    // impersonator: it has no close handler and disables escape, and accepting would
    // silently record nothing, so it would simply reopen.
    if (impersonation?.active) {
        return null
    }

    if (
        isLoading ||
        isFetching ||
        !reacceptanceStatus ||
        reacceptanceStatus.accepted === true
    ) {
        return null
    }

    return (
        <Dialog open maxWidth="md" fullWidth disableEscapeKeyDown>
            <DialogTitle>New Terms of Service</DialogTitle>
            <DialogContent dividers>
                <>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Summary
                    </Typography>
                    <ReactMarkdown>{reacceptanceStatus.summary}</ReactMarkdown>

                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Full Terms & Services
                    </Typography>
                    <ReactMarkdown>{reacceptanceStatus.content}</ReactMarkdown>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={checked}
                                onChange={(e) => setChecked(e.target.checked)}
                                color="primary"
                                disabled={isConfirming}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I have read and agree to the Terms of Service
                            </Typography>
                        }
                        sx={{ mt: 2 }}
                    />

                    {confirmationError && (
                        <Alert severity="error">
                            {
                                parseLaravelError(confirmationError as any)
                                    .message
                            }
                        </Alert>
                    )}
                </>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAccept}
                    disabled={!checked || isConfirming}
                    startIcon={
                        isConfirming ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : null
                    }
                >
                    {isConfirming ? 'Accepting...' : 'Accept and Continue'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default TermsOfServiceModal
